import { isEqual } from "lodash-es";
import {
    type ComponentProps,
    type ComponentType,
    useContext,
    useEffect,
    useMemo,
    useRef,
} from "react";
import { useParams } from "react-router";
import { Route } from "react-router-dom";

import { type PageName } from "@btnet/utilities-analytics";
import {
    type IWebviewRouteParams,
    mobile,
    useBtMobileContext,
    WebviewContextProvider,
} from "@btnet/utilities-webview";

import { AppProvider } from "helpers/AppProvider";
import {
    type AppProviderHandler,
    defaultAppProviderHandler,
} from "helpers/AppProvider.api.handler";
import { BTLinkSpaRoutingProvider } from "helpers/globalContext/BTLinkSpaRoutingProvider";
import { BuilderInfoContext } from "helpers/globalContext/BuilderInfoContext";
import { DefaultEnabledSpaInfo, SpaInfoContext } from "helpers/globalContext/SPAInfoContext";

import { type IAPIHandlerResult } from "types/apiResponse/apiResponse";
import { BTSessionStorage } from "types/btStorage";

import { track } from "utilities/analytics/analytics";
import { stringToArray } from "utilities/string/string";
import { useQueryString } from "utilities/url/url";

import { FocusProvider } from "commonComponents/utilities/Focus/FocusProvider";
import {
    defaultJobPickerHandler,
    type IJobPickerHandler,
} from "commonComponents/utilities/JobPicker/JobPicker.api.handler";
import { type JobPickerSetResponse } from "commonComponents/utilities/JobPicker/JobPicker.api.types";
import PageTitle from "commonComponents/utilities/PageTitle/PageTitle";

import { AnalyticsPageContext } from "./AnalyticsPageContext";

export interface IWebContainerProps {
    selectedJobIds: number[];
    generalJobId?: number;
}

/**
 * Whether a selection can be persisted to the server session.
 *
 * SetJobPickerData only accepts real jobsites, which always have a positive id -- the server tests
 * the same way (LegacyService.GetCurrentJobsite, TimeClockLegacyService.GetJobIdDefaultForSummary).
 * Its access check (LegacyService.VerifyJobsiteAccess) drops every JobIdTypes sentinel -- AllJobs
 * (0), NoJobs (-1), GlobalDocs (-2), PrintJobs (-3), GlobalJob (-4) -- and then throws
 * BTNoJobSelectedException when nothing is left to verify, so an all-sentinel selection fails
 * exactly like an empty one. Detail routes reach here with a sentinel whenever a placeholder job
 * segment stands in for "no real job" (eg a general-job shift).
 */
function canPersistToSession(jobIds: number[]): boolean {
    return jobIds.some((id) => id > 0);
}

/**
 * We need to set the job in the backend to stay in sync with non-SPA pages. Delete this when we no longer depend on selected jobIds in server session.
 * @param jobIds to set in server session
 * @param jobPickerHandler handler for setting job picker state
 */
function useSetLegacySessionJobIds(jobIds: number[], jobPickerHandler: IJobPickerHandler) {
    const builderInfo = useContext(BuilderInfoContext);
    const builderId = builderInfo?.builderId ?? 0;
    const previousResultRef = useRef<IAPIHandlerResult<JobPickerSetResponse> | undefined>();
    useEffect(() => {
        // An empty selection is never a valid payload for SetJobPickerData:
        // the server dereferences SelectedJobId before any null-guard, and
        // the mobile shell can push an empty array after the session is gone.
        // A sentinel-only selection is rejected the same way -- see canPersistToSession.
        if (!canPersistToSession(jobIds)) {
            return;
        }

        const selectedJobIds = jobIds.map((id) => ({ builderId, jobId: id }));
        const selectedJobId = selectedJobIds[0];
        previousResultRef.current = jobPickerHandler.set(
            selectedJobId,
            selectedJobIds,
            false,
            undefined,
            false,
        );

        return () => {
            if (previousResultRef.current) {
                previousResultRef.current.cancel();
                previousResultRef.current = undefined;
            }
        };
    }, [builderId, jobIds, jobPickerHandler]);
}

function useJobIdState() {
    const params = useParams<{ jobId?: string }>();
    const qsValues = useQueryString<{ jobIds?: string; jobId?: string }>();

    const jobIdsRef = useRef<number[]>([]);

    const mobileContext = useBtMobileContext();
    const mobileContextJobIds = mobileContext?.selectedJobIds;
    const mobileContextGeneralJobId = mobileContext?.generalJobId;

    const jobIds = useMemo(() => {
        const storageJobIds = BTSessionStorage.get("bt-numberArray-webviewSelectedJobIds");
        let result: number[] | undefined;
        if (mobileContextJobIds !== undefined) {
            // Mobile context is authoritative. Persist so the value survives in-webview
            // navigation (events only fire on picker change, not per route transition).
            result = mobileContextJobIds;
            BTSessionStorage.set("bt-numberArray-webviewSelectedJobIds", result);
        } else if (qsValues.jobIds || qsValues.jobId) {
            result = stringToArray(qsValues.jobIds ?? qsValues.jobId);
            // We only want to persist query string param jobIds because they will be stripped off on navigation
            BTSessionStorage.set("bt-numberArray-webviewSelectedJobIds", result);
        } else if (storageJobIds.length > 0) {
            result = storageJobIds;
        } else if (params.jobId !== undefined) {
            const parsedJobId = parseInt(params.jobId);
            if (!isNaN(parsedJobId)) {
                result = [parsedJobId];
            }
        }

        /* eslint-disable react-hooks/refs */
        // All paths above result in a new array instance, this ensures that the reference only changes when the actual jobIds change
        if (isEqual(jobIdsRef.current, result)) {
            return jobIdsRef.current;
        }
        jobIdsRef.current = result ?? storageJobIds;
        /* eslint-enable react-hooks/refs */

        return result ?? storageJobIds;
    }, [mobileContextJobIds, params.jobId, qsValues.jobId, qsValues.jobIds]);

    const jobIdToTrack = useMemo(() => {
        if (params.jobId) {
            return parseInt(params.jobId);
        } else if (jobIds.length === 1) {
            return jobIds[0];
        }
        return undefined;
    }, [jobIds, params.jobId]);

    return { jobIds, jobIdToTrack, generalJobId: mobileContextGeneralJobId };
}

// Components
interface IRoutedComponentProps {
    component: ComponentType<IWebContainerProps>;
    jobPickerHandler: IJobPickerHandler;
    rootPath: string;
    analyticsPageName?: PageName;
}

const RoutedComponent = ({
    component: Component,
    jobPickerHandler,
    rootPath,
    analyticsPageName,
}: IRoutedComponentProps) => {
    const { jobIds, jobIdToTrack, generalJobId } = useJobIdState();

    useSetLegacySessionJobIds(jobIds, jobPickerHandler);

    useEffect(() => {
        if (jobIdToTrack !== undefined) {
            track({ jobId: jobIdToTrack });
        }
    }, [jobIds, jobIdToTrack]);

    const { platform, mobileVersion } = useParams<IWebviewRouteParams>();
    const webviewContextValue = useMemo<ComponentProps<typeof WebviewContextProvider>["value"]>(
        () => ({
            platform: platform as IWebviewRouteParams["platform"],
            mobileVersion,
            rootPath,
            native: mobile,
        }),
        [platform, mobileVersion, rootPath],
    );

    return (
        <WebviewContextProvider value={webviewContextValue}>
            <AnalyticsPageContext pageName={analyticsPageName}>
                <Component selectedJobIds={jobIds} generalJobId={generalJobId} />
            </AnalyticsPageContext>
        </WebviewContextProvider>
    );
};

interface IWebviewPageLayoutProps {
    component: ComponentType<IWebContainerProps>;
    path: string;
    exact?: boolean;
    /**
     * Title to be displayed in the browser tab
     */
    title: string;
    appProviderHandler?: AppProviderHandler;
    jobPickerHandler?: IJobPickerHandler;

    /**
     * Seeds the Sawmill v3 analytics `pageName` for this route, so every event
     * emitted anywhere beneath the layout carries it without each page having
     * to wrap itself in its own context provider.
     *
     * Add the literal to `PageName` in the analytics package's
     * `registry/pageNames.ts` first. Omit for pages not yet on v3 — the
     * analytics context is simply left unseeded.
     */
    analyticsPageName?: PageName;
}

export const WebviewPageLayout = ({
    component,
    path,
    exact,
    title,
    appProviderHandler = defaultAppProviderHandler,
    jobPickerHandler = defaultJobPickerHandler,
    analyticsPageName,
}: IWebviewPageLayoutProps) => {
    const origNavHeightRef = useRef<string>("");

    useEffect(() => {
        origNavHeightRef.current = document.documentElement.style.getPropertyValue(
            "--main-navigation-height",
        );
        document.documentElement.style.setProperty("--main-navigation-height", "0px");

        return () => {
            document.documentElement.style.setProperty(
                "--main-navigation-height",
                origNavHeightRef.current,
            );
        };
    }, []);

    return (
        <AppProvider handler={appProviderHandler}>
            <FocusProvider shareFocusWithParent>
                <SpaInfoContext.Provider value={DefaultEnabledSpaInfo}>
                    <BTLinkSpaRoutingProvider>
                        <Route path={path} exact={exact}>
                            <PageTitle title={title} />
                            <RoutedComponent
                                component={component}
                                jobPickerHandler={jobPickerHandler}
                                rootPath={path}
                                analyticsPageName={analyticsPageName}
                            />
                        </Route>
                    </BTLinkSpaRoutingProvider>
                </SpaInfoContext.Provider>
            </FocusProvider>
        </AppProvider>
    );
};
